import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { AccountBreakdownDataPoint } from '../types/AssetClasses';

interface PortfolioChartProps {
  data: AccountBreakdownDataPoint[];
  allSimulationRuns: AccountBreakdownDataPoint[][];
  isLoading: boolean;
}

const PortfolioChart: React.FC<PortfolioChartProps> = ({ data, allSimulationRuns, isLoading }) => {
  const [selectedSimulation, setSelectedSimulation] = useState<number>(Math.floor(allSimulationRuns.length / 2)); // Default to median
  
  if (isLoading) {
    return (
      <div className="chart-container">
        <h2>Account Breakdown Over Time</h2>
        <div className="loading">Running Monte Carlo simulation...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="chart-container">
        <h2>Account Breakdown Over Time</h2>
        <div className="no-data">No simulation data available. Please run a simulation.</div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatYAxisValue = (value: number) => {
    return formatCurrency(value);
  };

  // Get the selected simulation data
  const getSelectedSimulationData = () => {
    if (allSimulationRuns.length === 0) return data;
    return allSimulationRuns[selectedSimulation] || data;
  };

  const getChartData = () => {
    const selectedData = getSelectedSimulationData();
      return selectedData;
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const validEntries = payload.filter((entry: any) => entry.value !== null && entry.value !== undefined);
      
      if (validEntries.length === 0) {
        return (
          <div className="custom-tooltip">
            <p className="tooltip-label">{`Year ${label}`}</p>
            <p style={{ color: '#718096', fontStyle: 'italic' }}>All accounts depleted</p>
          </div>
        );
      }

      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`Year ${label}`}</p>
          {validEntries.map((entry: any, index: number) => {
            const selectedData = getSelectedSimulationData();
            const originalPoint = selectedData[label - 1];
            const originalValue = originalPoint ? originalPoint[entry.dataKey as keyof AccountBreakdownDataPoint] as number : entry.value;
            return (
              <p key={index} style={{ color: entry.color }}>
                {`${entry.name}: ${formatCurrency(originalValue)}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h2>Account Type Balances Over Time (per simulation)</h2>
        <div className="chart-controls">
          <div className="simulation-selector">
            <label htmlFor="simulation-select">Simulation Run:</label>
            <select
              id="simulation-select"
              value={selectedSimulation}
              onChange={(e) => setSelectedSimulation(parseInt(e.target.value))}
            >
              {allSimulationRuns.map((_, index) => (
                <option key={index} value={index}>
                  Run {index + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <p className="chart-description">
        This chart shows how your portfolio is distributed across different account types over time.
        Select any simulation run to see how that specific scenario would play out.
      </p>
      
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={500}>
          <AreaChart
            data={getChartData()}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
            stackOffset="none"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
              tickFormatter={(value) => `${value}`}
            />
            <YAxis 
              width={110}
              tickMargin={8}
              label={{ value: 'Portfolio Value', angle: -90, position: 'insideLeft', offset: 0, style: { textAnchor: 'middle' }  }}
              tickFormatter={formatYAxisValue}
              scale='linear'
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            <Area
              type="monotone"
              dataKey="cashSavings"
              stackId="1"
              stroke="#4CAF50"
              fill="#4CAF50"
              name="Cash Savings"
            />
            <Area
              type="monotone"
              dataKey="taxDeferred"
              stackId="1"
              stroke="#2196F3"
              fill="#2196F3"
              name="Tax Deferred (401k, etc.)"
            />
            <Area
              type="monotone"
              dataKey="taxableInvestment"
              stackId="1"
              stroke="#FF9800"
              fill="#FF9800"
              name="Taxable Investment"
            />
            <Area
              type="monotone"
              dataKey="taxFree"
              stackId="1"
              stroke="#9C27B0"
              fill="#9C27B0"
              name="Tax Free (Roth, HSA)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-legend">
        <h3>Account Types</h3>
        <ul>
          <li><strong>Cash Savings:</strong> Withdrawn first, always 100% cash allocation</li>
          <li><strong>Tax Deferred (401k, etc.):</strong> Withdrawn second, subject to income tax</li>
          <li><strong>Taxable Investment:</strong> Withdrawn third, subject to capital gains tax</li>
          <li><strong>Tax Free (Roth, HSA):</strong> Withdrawn last, no tax on qualified withdrawals</li>
        </ul>
      </div>
    </div>
  );
};

export default PortfolioChart;
